import { supabase } from '../lib/supabase';

/**
 * Fetch blocked dates for a trailer listing
 * @param {string} trailerListingId - Trailer listing ID
 * @returns {Promise<Array>} Array of blocked dates
 */
export const fetchBlockedDates = async (trailerListingId) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.select('*')
      ?.eq('trailer_listing_id', trailerListingId)
      ?.eq('is_active', true)
      ?.order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching blocked dates:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchBlockedDates:', error);
    return [];
  }
};

/**
 * Fetch all blocked dates for owner's listings
 * @param {string} ownerId - Owner user ID
 * @returns {Promise<Array>} Array of blocked dates
 */
export const fetchOwnerBlockedDates = async (ownerId) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.select(`
        *,
        trailer:trailer_listings!blocked_dates_trailer_listing_id_fkey(
          id,
          title
        )
      `)
      ?.eq('owner_id', ownerId)
      ?.eq('is_active', true)
      ?.order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching owner blocked dates:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchOwnerBlockedDates:', error);
    return [];
  }
};

/**
 * Create a single blocked date
 * @param {Object} blockData - Blocked date data
 * @returns {Promise<Object|null>} Created blocked date
 */
export const createBlockedDate = async (blockData) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.insert([blockData])
      ?.select()
      ?.single();

    if (error) {
      console.error('Error creating blocked date:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in createBlockedDate:', error);
    throw error;
  }
};

/**
 * Create multiple blocked dates (bulk operation)
 * @param {Array} blockDataArray - Array of blocked date objects
 * @returns {Promise<Array>} Array of created blocked dates
 */
export const createBulkBlockedDates = async (blockDataArray) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.insert(blockDataArray)
      ?.select();

    if (error) {
      console.error('Error creating bulk blocked dates:', error?.message);
      throw error;
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in createBulkBlockedDates:', error);
    throw error;
  }
};

/**
 * Block a date range
 * @param {string} trailerListingId - Trailer listing ID
 * @param {string} ownerId - Owner user ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} reason - Reason for blocking
 * @returns {Promise<Object|null>} Created blocked date range
 */
export const blockDateRange = async (trailerListingId, ownerId, startDate, endDate, reason = '') => {
  try {
    const blockData = {
      trailer_listing_id: trailerListingId,
      owner_id: ownerId,
      block_type: 'range',
      start_date: startDate,
      end_date: endDate,
      reason: reason,
      is_active: true
    };

    return await createBlockedDate(blockData);
  } catch (error) {
    console.error('Error in blockDateRange:', error);
    throw error;
  }
};

/**
 * Block multiple single dates (bulk)
 * @param {string} trailerListingId - Trailer listing ID
 * @param {string} ownerId - Owner user ID
 * @param {Array<string>} dates - Array of dates (YYYY-MM-DD)
 * @param {string} reason - Reason for blocking
 * @returns {Promise<Array>} Array of created blocked dates
 */
export const blockMultipleDates = async (trailerListingId, ownerId, dates, reason = '') => {
  try {
    const blockDataArray = dates?.map(date => ({
      trailer_listing_id: trailerListingId,
      owner_id: ownerId,
      block_type: 'single',
      start_date: date,
      end_date: null,
      reason: reason,
      is_active: true
    }));

    return await createBulkBlockedDates(blockDataArray);
  } catch (error) {
    console.error('Error in blockMultipleDates:', error);
    throw error;
  }
};

/**
 * Apply unavailability template
 * @param {string} trailerListingId - Trailer listing ID
 * @param {string} ownerId - Owner user ID
 * @param {string} templateType - Template type ('weekend', 'holiday', 'seasonal')
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} templateName - Template name
 * @param {string} description - Template description
 * @returns {Promise<Object|null>} Created template block
 */
export const applyTemplate = async (trailerListingId, ownerId, templateType, startDate, endDate, templateName, description = '') => {
  try {
    const blockTypeMap = {
      'weekend': 'template_weekend',
      'holiday': 'template_holiday',
      'seasonal': 'template_seasonal'
    };

    const blockData = {
      trailer_listing_id: trailerListingId,
      owner_id: ownerId,
      block_type: blockTypeMap?.[templateType] || 'range',
      start_date: startDate,
      end_date: endDate,
      template_name: templateName,
      template_description: description,
      is_active: true
    };

    return await createBlockedDate(blockData);
  } catch (error) {
    console.error('Error in applyTemplate:', error);
    throw error;
  }
};

/**
 * Toggle booking pause for a listing
 * @param {string} trailerListingId - Trailer listing ID
 * @param {string} ownerId - Owner user ID
 * @param {boolean} isPaused - Whether to pause or unpause
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD) - optional for indefinite pause
 * @returns {Promise<Object|null>} Created or updated booking pause
 */
export const toggleBookingPause = async (trailerListingId, ownerId, isPaused, startDate, endDate = null) => {
  try {
    if (!isPaused) {
      // Deactivate existing booking pause
      const { error } = await supabase
        ?.from('blocked_dates')
        ?.update({ is_active: false })
        ?.eq('trailer_listing_id', trailerListingId)
        ?.eq('block_type', 'booking_pause')
        ?.eq('is_active', true);

      if (error) throw error;
      return { success: true, paused: false };
    } else {
      // Create new booking pause
      const blockData = {
        trailer_listing_id: trailerListingId,
        owner_id: ownerId,
        block_type: 'booking_pause',
        start_date: startDate,
        end_date: endDate,
        reason: 'Booking paused by owner',
        is_active: true
      };

      const result = await createBlockedDate(blockData);
      return { ...result, paused: true };
    }
  } catch (error) {
    console.error('Error in toggleBookingPause:', error);
    throw error;
  }
};

/**
 * Check if listing has active booking pause
 * @param {string} trailerListingId - Trailer listing ID
 * @returns {Promise<Object|null>} Active booking pause or null
 */
export const getActiveBookingPause = async (trailerListingId) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.select('*')
      ?.eq('trailer_listing_id', trailerListingId)
      ?.eq('block_type', 'booking_pause')
      ?.eq('is_active', true)
      ?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('Error fetching booking pause:', error?.message);
      return null;
    }

    return data ? convertToCamelCase(data) : null;
  } catch (error) {
    console.error('Error in getActiveBookingPause:', error);
    return null;
  }
};

/**
 * Delete a blocked date
 * @param {string} blockedDateId - Blocked date ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteBlockedDate = async (blockedDateId) => {
  try {
    const { error } = await supabase
      ?.from('blocked_dates')
      ?.delete()
      ?.eq('id', blockedDateId);

    if (error) {
      console.error('Error deleting blocked date:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteBlockedDate:', error);
    throw error;
  }
};

/**
 * Delete multiple blocked dates
 * @param {Array<string>} blockedDateIds - Array of blocked date IDs
 * @returns {Promise<boolean>} Success status
 */
export const deleteBulkBlockedDates = async (blockedDateIds) => {
  try {
    const { error } = await supabase
      ?.from('blocked_dates')
      ?.delete()
      ?.in('id', blockedDateIds);

    if (error) {
      console.error('Error deleting bulk blocked dates:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteBulkBlockedDates:', error);
    throw error;
  }
};

/**
 * Update a blocked date
 * @param {string} blockedDateId - Blocked date ID
 * @param {Object} updates - Updated data
 * @returns {Promise<Object|null>} Updated blocked date
 */
export const updateBlockedDate = async (blockedDateId, updates) => {
  try {
    const { data, error } = await supabase
      ?.from('blocked_dates')
      ?.update(updates)
      ?.eq('id', blockedDateId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating blocked date:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in updateBlockedDate:', error);
    throw error;
  }
};

/**
 * Convert snake_case to camelCase
 */
function convertToCamelCase(obj) {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(convertToCamelCase);
  if (typeof obj !== 'object') return obj;

  const camelCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelCaseObj[camelKey] = typeof value === 'object' ? convertToCamelCase(value) : value;
  }
  return camelCaseObj;
}

export const blockedDatesService = {
  fetchBlockedDates,
  fetchOwnerBlockedDates,
  createBlockedDate,
  createBulkBlockedDates,
  blockDateRange,
  blockMultipleDates,
  applyTemplate,
  toggleBookingPause,
  getActiveBookingPause,
  deleteBlockedDate,
  deleteBulkBlockedDates,
  updateBlockedDate
};